// Phase 54.0 — Backup & Disaster Recovery UI.
//
// Super admins land here to monitor system health. Authorised backup
// admins (Burak, cemil per Phase 54.0 spec) additionally see the
// destructive controls: Create, Download, Delete, Restore. Everyone else
// gets read-only cards + history plus disabled buttons with an
// explanatory tooltip so operators immediately understand why they can't
// click.

import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import usmApi from "../../lib/usmApi";
import { useUsmAuth } from "@/contexts/UsmAuthContext";
import HubTabs from "./hub/HubTabs";
import FavoriteStar from "./hub/FavoriteStar";
import {
  Database, Download, PlusCircle, RotateCcw, Trash2, AlertTriangle,
  ShieldCheck, Clock, HardDrive, User, CheckCircle2, XCircle, Loader2,
} from "lucide-react";

const TYPE_LABELS = {
  manual:    "Manuel",
  daily:     "Günlük",
  weekly:    "Haftalık",
  monthly:   "Aylık",
  emergency: "Acil Durum",
};

const PHASE_LABELS = {
  starting:              "başlatılıyor",
  dump_collections:      "veritabanı yazılıyor",
  bundle_legacy_media:   "yerel medya paketleniyor",
  bundle_object_storage: "medya indiriliyor",
  finalising_zip:        "ZIP kapatılıyor",
  hashing:               "SHA-256 hesaplanıyor",
  done:                  "tamamlandı",
  aborted:               "iptal edildi",
};

const STATUS_STYLES = {
  success:     { icon: CheckCircle2, cls: "text-emerald-700 bg-emerald-50 border-emerald-200", label: "Başarılı" },
  in_progress: { icon: Loader2,      cls: "text-amber-700 bg-amber-50 border-amber-200",     label: "Sürüyor…" },
  failed:      { icon: XCircle,      cls: "text-red-700 bg-red-50 border-red-200",           label: "Başarısız" },
  deleted:     { icon: Trash2,       cls: "text-stone-600 bg-stone-100 border-stone-200",    label: "Silinmiş" },
  rotated:     { icon: RotateCcw,    cls: "text-stone-600 bg-stone-100 border-stone-200",    label: "Rotasyon" },
};

function fmtDate(s) {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleString("tr-TR", { dateStyle: "medium", timeStyle: "short" });
  } catch { return s; }
}

export default function BackupPage() {
  const { employee } = useUsmAuth();
  const [dashboard, setDashboard] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState({ can_manage: false, authorized_users: [] });
  const [creating, setCreating] = useState(false);
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [diagTarget, setDiagTarget] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const [d, r, p] = await Promise.all([
        usmApi.get("/system/backups/dashboard"),
        usmApi.get("/system/backups/"),
        usmApi.get("/system/backups/permissions"),
      ]);
      setDashboard(d.data);
      setRows(r.data || []);
      setPermissions(p.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Yedekleme verisi yüklenemedi");
    } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  // Phase 54.9 — live polling for `in_progress` rows so a manual or
  // scheduled backup doesn't appear "stuck" in the UI. As soon as any
  // history row is in progress we re-fetch every 4s until all rows are
  // terminal (success / failed / deleted / rotated). Polling stops on
  // its own when there's nothing left to watch.
  const hasInProgress = useMemo(
    () => rows.some((r) => r.status === "in_progress"),
    [rows],
  );
  useEffect(() => {
    if (!hasInProgress) return undefined;
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      try {
        const [d, r] = await Promise.all([
          usmApi.get("/system/backups/dashboard"),
          usmApi.get("/system/backups/"),
        ]);
        if (cancelled) return;
        setDashboard(d.data);
        const next = r.data || [];
        // Detect terminal transitions since last poll and notify the user
        // (the create-backup path resolves before completion, so this is
        // the only place a "success/failure" toast can fire reliably).
        setRows((prev) => {
          const prevById = new Map(prev.map((x) => [x.id, x]));
          next.forEach((n) => {
            const p = prevById.get(n.id);
            if (p && p.status === "in_progress" && n.status !== "in_progress") {
              if (n.status === "success") {
                toast.success(`Yedek tamamlandı · ${n.filename}`);
              } else if (n.status === "failed") {
                toast.error(`Yedek başarısız · ${n.error || "bilinmeyen hata"}`);
              }
            }
          });
          return next;
        });
      } catch (_) { /* transient — try again on next tick */ }
    };
    const id = window.setInterval(tick, 4000);
    return () => { cancelled = true; window.clearInterval(id); };
  }, [hasInProgress]);

  const canManage = !!permissions.can_manage;
  const disabledTip = "Yalnızca Yedekleme Yöneticileri bu işlemi yapabilir.";

  const createBackup = async () => {
    if (!canManage) return;
    setCreating(true);
    try {
      const r = await usmApi.post("/system/backups/");
      // Phase 54.9 — the endpoint returns immediately with an
      // `in_progress` row. The `hasInProgress` polling effect above will
      // update the UI live and fire a success / failure toast when the
      // background job completes.
      toast.info(`Yedekleme başlatıldı · ${r.data.filename} · arka planda çalışıyor…`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Yedek oluşturulamadı");
    } finally { setCreating(false); }
  };

  const downloadBackup = async (row) => {
    if (!canManage) return;
    try {
      // usmApi is axios; force blob so a valid ZIP can be handed to the browser
      // regardless of the interceptor's default JSON parsing.
      const r = await usmApi.get(`/system/backups/${row.id}/download`, {
        responseType: "blob",
      });

      // Defensive: the response *must* be a Blob at this point. If an
      // interceptor or a proxy replaced the body with a JSON error, wrapping
      // that in `new Blob([...])` would silently produce a corrupted archive
      // — refuse it up-front instead so the operator sees a real error.
      const data = r?.data;
      if (!(data instanceof Blob) || data.size === 0) {
        throw new Error("Sunucudan geçerli bir ZIP yanıtı alınamadı");
      }

      // Use the axios-provided Blob *directly*. Re-wrapping it in
      // `new Blob([data], { type: "application/zip" })` was a no-op in the
      // happy path but hid real errors when `data` wasn't a Blob.
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = row.filename;
      a.rel = "noopener";
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();

      // IMPORTANT — do NOT revoke the object URL or detach the <a> synchronously.
      // Chrome, Safari and Firefox all need at least one event-loop tick to
      // attach the download stream to the anchor before it is released.
      // Revoking too early aborts the download on Safari/iPad and occasionally
      // on Chrome for large ZIPs. 500ms is well within the safe window used
      // by production apps (FileSaver.js uses 40s; we only need one tick).
      window.setTimeout(() => {
        try { a.remove(); } catch (e) { /* already detached */ }
        try { URL.revokeObjectURL(url); } catch (e) { /* already released */ }
      }, 500);

      toast.success(`${row.filename} indirildi`);
      // Server logs download in audit; refresh dashboard to show it.
      window.setTimeout(refresh, 800);
    } catch (err) {
      // Surface the real reason so the failure is diagnosable in production
      // instead of a silent "Yedek indirilemedi" (Phase 54.5 root cause).
      // If the server returned a JSON error body but responseType was "blob",
      // it arrives as a Blob — try to read it back as text before giving up.
      let detail = err?.response?.data?.detail || err?.message || "";
      if (!detail && err?.response?.data instanceof Blob) {
        try { detail = await err.response.data.text(); } catch (_) { /* ignore */ }
      }
      console.error("[backup.download] failed:", err, detail);
      toast.error(detail ? `Yedek indirilemedi: ${detail}` : "Yedek indirilemedi");
    }
  };

  const deleteBackup = async (row) => {
    if (!canManage) return;
    if (!window.confirm(`${row.filename} silinsin mi? Bu işlem geri alınamaz.`)) return;
    try {
      await usmApi.delete(`/system/backups/${row.id}`);
      toast.success("Yedek silindi");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Silme başarısız");
    }
  };

  return (
    <div data-testid="usm-backup-page" className="space-y-8">
      <HubTabs section="system" />
      <header className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="font-heading text-2xl">Yedekleme & Kurtarma</h1>
            <FavoriteStar />
          </div>
          <p className="text-xs text-[#71717A] mt-1 tracking-wide">
            Sistemin tamamı için otomatik + manuel yedekleme, doğrulama, geri yükleme.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717A]">Yetkili Kullanıcı</div>
            <div className="text-sm font-medium mt-0.5" data-testid="backup-current-user">
              {employee?.username || "—"}
              {canManage && <span className="ml-2 inline-block px-1.5 py-0.5 bg-[#0A0A0A] text-white text-[9.5px] uppercase tracking-widest">Yedekleme Yöneticisi</span>}
            </div>
          </div>
          <button
            data-testid="backup-create-btn"
            onClick={createBackup}
            disabled={!canManage || creating}
            title={canManage ? "" : disabledTip}
            className="inline-flex items-center gap-2 bg-[#0A0A0A] text-white text-xs uppercase tracking-[0.2em] px-5 py-3 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {creating ? <Loader2 size={14} className="animate-spin" /> : <PlusCircle size={14} />}
            {creating ? "Oluşturuluyor…" : "Yedek Oluştur"}
          </button>
        </div>
      </header>

      {/* Phase 54.9.2 — operator warning. When a backup is in progress
          any interruption to the pod (redeploy, hot reload) will kill
          it and the row becomes "aborted (server restart)". Loudly
          tell the operator so they don't shoot themselves in the foot
          mid-backup. */}
      {hasInProgress && (
        <div
          data-testid="backup-in-progress-banner"
          className="border border-amber-300 bg-amber-50 text-amber-900 px-4 py-3 text-[12px] tracking-wide flex items-start gap-3"
        >
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <div>
            <div className="font-semibold uppercase tracking-[0.2em] text-[10.5px]">
              Yedekleme Sürüyor
            </div>
            <div className="mt-1">
              Bir yedekleme aktif olarak çalışıyor. Bu sırada uygulamayı
              yeniden dağıtmayın (redeploy) — dağıtım işlemi yedeklemeyi
              iptal eder ve geçmişte <span className="font-mono">aborted (server restart)</span> olarak kalır.
              Yedeklemenin tamamlanmasını bekleyin.
            </div>
          </div>
        </div>
      )}

      {/* Dashboard cards */}
      {dashboard && (
        <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <Card data-testid="card-latest-backup" icon={ShieldCheck} title="Son Yedek">
            {dashboard.latest_backup ? (
              <>
                <div className="font-finance text-lg">{fmtDate(dashboard.latest_backup.created_at)}</div>
                <div className="text-[11px] text-[#71717A]">{TYPE_LABELS[dashboard.latest_backup.type] || dashboard.latest_backup.type} · {dashboard.latest_backup.filename}</div>
              </>
            ) : <p className="text-sm text-[#A8A29E]">Henüz yedek yok</p>}
          </Card>
          <Card data-testid="card-next-scheduled" icon={Clock} title="Sıradaki Zamanlı Yedek">
            <div className="font-finance text-lg">{fmtDate(dashboard.next_scheduled)}</div>
            <div className="text-[11px] text-[#71717A]">Günlük · 02:00 UTC</div>
          </Card>
          <Card data-testid="card-total-backups" icon={Database} title="Toplam Yedek">
            <div className="font-finance text-lg">{dashboard.total_backups}</div>
            <div className="text-[11px] text-[#71717A]">Başarılı yedek sayısı</div>
          </Card>
          <Card data-testid="card-storage" icon={HardDrive} title="Kullanılan Depolama">
            <div className="font-finance text-lg">{dashboard.storage_used_human}</div>
            <div className="text-[11px] text-[#71717A]">{dashboard.total_backups} arşiv</div>
          </Card>
          <Card data-testid="card-latest-manual" icon={PlusCircle} title="Son Manuel Yedek">
            {dashboard.latest_manual ? (
              <>
                <div className="font-finance text-lg">{fmtDate(dashboard.latest_manual.created_at)}</div>
                <div className="text-[11px] text-[#71717A]">{dashboard.latest_manual.created_by_username || "—"}</div>
              </>
            ) : <p className="text-sm text-[#A8A29E]">Yok</p>}
          </Card>
          <Card data-testid="card-latest-download" icon={Download} title="Son İndirme">
            {dashboard.latest_download ? (
              <>
                <div className="font-finance text-lg">{dashboard.latest_download.username || "—"}</div>
                <div className="text-[11px] text-[#71717A]">{fmtDate(dashboard.latest_download.at)}</div>
              </>
            ) : <p className="text-sm text-[#A8A29E]">Kayıt yok</p>}
          </Card>
        </section>
      )}

      {!loading && !canManage && (
        <div data-testid="backup-permission-notice" className="bg-amber-50 border border-amber-200 text-amber-900 text-sm px-4 py-3 flex items-start gap-2">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <div>
            Yedekleme işlemleri yalnızca yetkili yöneticiler tarafından yapılabilir:{" "}
            <b>{permissions.authorized_users?.join(", ") || "—"}</b>. Diğer süper yöneticiler yalnızca görüntüleyebilir.
          </div>
        </div>
      )}

      {/* History */}
      <section>
        <div className="flex items-center gap-3 mb-3">
          <h2 className="text-sm uppercase tracking-[0.2em] text-[#71717A]">Yedekleme Geçmişi</h2>
          <span className="text-[10.5px] text-[#A8A29E]">{rows.length} kayıt</span>
        </div>
        {loading ? <div className="py-16 text-center text-sm text-[#71717A]">Yükleniyor…</div> : (
          <div className="overflow-x-auto bg-white border border-[#E7E5E4]">
            <table className="min-w-full text-sm">
              <thead className="bg-[#FAFAF9] text-[10.5px] uppercase tracking-[0.15em] text-[#71717A]">
                <tr>
                  <th className="text-left px-3 py-2">Tarih</th>
                  <th className="text-left px-3 py-2">Tür</th>
                  <th className="text-left px-3 py-2">Boyut</th>
                  <th className="text-left px-3 py-2">Durum</th>
                  <th className="text-left px-3 py-2">Oluşturan</th>
                  <th className="text-right px-3 py-2">İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={6} className="text-center py-10 text-[#A8A29E] text-sm">Kayıt yok. İlk yedeği oluşturun.</td></tr>
                ) : rows.map((r) => {
                  const st = STATUS_STYLES[r.status] || { icon: CheckCircle2, cls: "text-stone-600 bg-stone-100 border-stone-200", label: r.status };
                  const StatusIcon = st.icon;
                  return (
                    <tr key={r.id} data-testid={`backup-row-${r.id}`} className="border-t border-[#F5F5F4]">
                      <td className="px-3 py-2 font-mono text-[11.5px]">{fmtDate(r.created_at)}</td>
                      <td className="px-3 py-2">{TYPE_LABELS[r.type] || r.type}</td>
                      <td className="px-3 py-2 font-mono">{r.size_human}</td>
                      <td className="px-3 py-2">
                        <span className={"inline-flex items-center gap-1 px-2 py-0.5 text-[10.5px] uppercase tracking-[0.15em] border " + st.cls}>
                          <StatusIcon size={10} className={r.status === "in_progress" ? "animate-spin" : ""} />
                          {st.label}
                        </span>
                        {/* Phase 54.9.1 — live sub-status so a running
                            backup shows WHERE it is (media progress, DB
                            dump, hashing…) and a failed backup shows the
                            actual error inline instead of a blank cell. */}
                        {r.status === "in_progress" && r.phase && (
                          <div
                            data-testid={`backup-phase-${r.id}`}
                            className="mt-1 text-[10px] text-amber-700"
                          >
                            {PHASE_LABELS[r.phase] || r.phase}
                            {r.media_expected > 0 && (
                              <span className="ml-1 font-mono">
                                {" · "}{r.media_progress || 0}/{r.media_expected}
                                {r.media_failed > 0 && (
                                  <span className="text-red-600"> ({r.media_failed} hata)</span>
                                )}
                              </span>
                            )}
                          </div>
                        )}
                        {r.status === "failed" && r.error && (
                          <div
                            data-testid={`backup-error-${r.id}`}
                            className="mt-1 text-[10px] text-red-700 max-w-[280px] truncate"
                            title={r.error}
                          >
                            {r.error}
                          </div>
                        )}
                        {r.status === "failed" && (
                          <button
                            data-testid={`backup-diagnostics-${r.id}`}
                            onClick={() => setDiagTarget(r)}
                            className="mt-1 text-[10px] text-[#0A0A0A] hover:text-[#C8A96A] underline underline-offset-2 tracking-wide"
                          >
                            Tanı Detayı →
                          </button>
                        )}
                      </td>
                      <td className="px-3 py-2 text-[11.5px]">
                        <span className="inline-flex items-center gap-1 text-[#52525B]"><User size={11} />{r.created_by_username || "sistem"}</span>
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <button
                          data-testid={`backup-download-${r.id}`}
                          onClick={() => downloadBackup(r)}
                          disabled={!canManage || r.status !== "success"}
                          title={!canManage ? disabledTip : ""}
                          className="inline-flex items-center gap-1 text-[11px] text-[#0A0A0A] hover:text-[#C8A96A] disabled:opacity-30 disabled:cursor-not-allowed mr-2"
                        ><Download size={12} /> İndir</button>
                        <button
                          data-testid={`backup-restore-${r.id}`}
                          onClick={() => canManage && setRestoreTarget(r)}
                          disabled={!canManage || r.status !== "success"}
                          title={!canManage ? disabledTip : ""}
                          className="inline-flex items-center gap-1 text-[11px] text-[#0A0A0A] hover:text-red-700 disabled:opacity-30 disabled:cursor-not-allowed mr-2"
                        ><RotateCcw size={12} /> Geri Yükle</button>
                        <button
                          data-testid={`backup-delete-${r.id}`}
                          onClick={() => deleteBackup(r)}
                          disabled={!canManage || r.status !== "success"}
                          title={!canManage ? disabledTip : ""}
                          className="inline-flex items-center gap-1 text-[11px] text-red-600 hover:text-red-800 disabled:opacity-30 disabled:cursor-not-allowed"
                        ><Trash2 size={12} /> Sil</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {restoreTarget && (
        <RestoreDialog
          row={restoreTarget}
          onClose={() => setRestoreTarget(null)}
          onDone={() => { setRestoreTarget(null); refresh(); }}
        />
      )}
      {diagTarget && (
        <DiagnosticsDialog
          row={diagTarget}
          onClose={() => setDiagTarget(null)}
        />
      )}
    </div>
  );
}

function Card({ icon: Icon, title, children, ...rest }) {
  return (
    <div className="bg-white border border-[#E7E5E4] p-4" {...rest}>
      <div className="flex items-center gap-2 mb-2">
        <Icon size={13} className="text-[#C8A96A]" />
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#71717A]">{title}</p>
      </div>
      {children}
    </div>
  );
}

function RestoreDialog({ row, onClose, onDone }) {
  const [step, setStep] = useState(1);
  const [phrase, setPhrase] = useState("");
  const [running, setRunning] = useState(false);
  const valid = phrase.trim().toUpperCase() === "RESTORE";

  const doRestore = async () => {
    setRunning(true);
    try {
      // Phase 54.6 — restore now runs as a background job so the HTTP
      // response returns immediately with a job id. Poll for completion
      // instead of waiting synchronously (Cloudflare 100s edge timeout).
      const start = await usmApi.post(
        `/system/backups/${row.id}/restore`,
        { confirm_phrase: phrase.trim().toUpperCase() },
      );
      const jobId = start.data?.id;
      if (!jobId) throw new Error("restore job id missing in response");
      toast.info("Geri yükleme başlatıldı — arka planda çalışıyor…");

      // Poll every 3s, up to 15 minutes.
      const started = Date.now();
      const MAX_MS = 15 * 60 * 1000;
      let final = null;
      for (;;) {
        await new Promise((res) => setTimeout(res, 3000));
        let poll;
        try {
          poll = await usmApi.get(`/system/backups/restore-jobs/${jobId}`);
        } catch (e) {
          if (Date.now() - started > MAX_MS) throw e;
          continue;
        }
        const st = poll.data?.status;
        if (st === "success" || st === "failed") { final = poll.data; break; }
        if (Date.now() - started > MAX_MS) {
          throw new Error("Geri yükleme zaman aşımı (15 dk)");
        }
      }

      if (final.status === "success") {
        const media = final.result?.media_restored ?? "?";
        const emerg = (final.emergency_backup_id || "").slice(0, 8);
        toast.success(`Geri yükleme tamamlandı · ${media} medya · Acil yedek: ${emerg}…`);
        onDone();
      } else {
        toast.error(final.error || "Geri yükleme başarısız");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "";
      toast.error(detail ? `Geri yükleme başarısız: ${detail}` : "Geri yükleme başarısız");
    } finally { setRunning(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div data-testid="restore-dialog" className="bg-white w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-[#E7E5E4] flex items-center justify-between">
          <div>
            <h2 className="font-heading text-lg">Yedekten Geri Yükle</h2>
            <p className="text-[11px] text-[#71717A] mt-0.5 font-mono">{row.filename}</p>
          </div>
          <button onClick={onClose} className="text-[#71717A]"><Trash2 size={18} /></button>
        </div>
        <div className="p-6 space-y-4">
          {step === 1 ? (
            <>
              <div className="bg-red-50 border border-red-200 text-red-900 text-sm p-4 flex items-start gap-2">
                <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                <div>
                  <b>Bu işlem mevcut tüm sistem verisini bu yedekle değiştirecek.</b><br />
                  <span className="text-[12px] mt-1 inline-block">Devam etmeden önce sistem otomatik olarak <b>Acil Durum Yedeği</b> alacak. İşlem geri alınamaz.</span>
                </div>
              </div>
              <div className="flex justify-end gap-3">
                <button onClick={onClose} className="text-xs uppercase tracking-[0.2em] px-5 py-3 hover:bg-[#FAFAF9]">İptal</button>
                <button
                  data-testid="restore-next"
                  onClick={() => setStep(2)}
                  className="bg-red-600 hover:bg-red-700 text-white text-xs uppercase tracking-[0.2em] px-6 py-3"
                >Anladım, Devam Et</button>
              </div>
            </>
          ) : (
            <>
              <p className="text-sm text-[#52525B]">Onaylamak için aşağıya <b>RESTORE</b> yazın:</p>
              <input
                data-testid="restore-phrase"
                autoFocus
                value={phrase}
                onChange={(e) => setPhrase(e.target.value)}
                placeholder="RESTORE"
                className="w-full px-3 py-3 border border-[#E7E5E4] focus:border-red-500 outline-none text-sm font-mono uppercase tracking-widest"
              />
              <div className="flex justify-end gap-3">
                <button onClick={onClose} disabled={running} className="text-xs uppercase tracking-[0.2em] px-5 py-3 hover:bg-[#FAFAF9] disabled:opacity-40">İptal</button>
                <button
                  data-testid="restore-confirm"
                  onClick={doRestore}
                  disabled={!valid || running}
                  className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-xs uppercase tracking-[0.2em] px-6 py-3"
                >
                  {running ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />}
                  {running ? "Geri yükleniyor…" : "Geri Yükle"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}


// Phase 54.11 — forensic diagnostics dialog.
//
// Renders EVERYTHING the backend's `/diagnostics` endpoint knows about
// a failed backup: crash diagnosis (POD_RESTART / PROCESS_RESTART /
// SIGTERM…), last completed phase, stage history with per-stage
// wall-clock ms + RSS delta, and — when the process managed to record
// one — a full Python traceback. Operators can pinpoint the killing
// stage in production without any shell access.
function DiagnosticsDialog({ row, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await usmApi.get(`/system/backups/${row.id}/diagnostics`);
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Tanı verisi alınamadı");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [row.id]);

  const dbRow = data?.row || {};
  const journal = data?.journal || {};
  const current = data?.current_process || {};
  // Merge db row + journal (journal wins for fresher forensic state).
  const merged = { ...dbRow, ...(journal || {}) };
  const timings = merged.stage_timings || {};
  const history = merged.stage_history || [];

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(data, null, 2));
      toast.success("Tanı verisi kopyalandı");
    } catch { toast.error("Kopyalanamadı"); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div
        data-testid="backup-diagnostics-dialog"
        className="bg-white w-full max-w-3xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-[#E7E5E4] flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <h2 className="font-heading text-lg">Yedek Tanı Detayı</h2>
            <p className="text-[11px] text-[#71717A] mt-0.5 font-mono">{row.filename}</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={copy}
              className="text-[10px] uppercase tracking-[0.2em] px-3 py-2 border border-[#E7E5E4] hover:bg-[#FAFAF9]"
            >Kopyala</button>
            <button onClick={onClose} className="text-[#71717A] hover:text-[#0A0A0A]">
              <XCircle size={18} />
            </button>
          </div>
        </div>
        <div className="p-6 space-y-6 text-sm">
          {loading && <div className="py-8 text-center text-[#71717A]">Yükleniyor…</div>}
          {error && <div className="text-red-700 text-xs">{error}</div>}
          {!loading && !error && (
            <>
              {/* Crash diagnosis banner */}
              <div className="border border-red-200 bg-red-50 p-4">
                <div className="text-[10px] uppercase tracking-[0.2em] text-red-700 mb-2">Hata Özeti</div>
                <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-[12px]">
                  <div><b>Tanı:</b> <span className="font-mono">{merged.crash_diagnosis || "—"}</span></div>
                  <div><b>Kilit Aşama:</b> <span className="font-mono">{merged.failed_phase || merged.phase || "—"}</span></div>
                  <div><b>Son Tamamlanan:</b> <span className="font-mono">{merged.last_completed_phase || "—"}</span></div>
                  <div><b>Sonlandırma:</b> <span className="font-mono">{merged.terminated_by || "—"}</span></div>
                  <div><b>Medya:</b> <span className="font-mono">{merged.media_progress || 0}/{merged.media_expected || 0} ({merged.media_failed || 0} hata)</span></div>
                  <div><b>Son RSS:</b> <span className="font-mono">{Math.round((merged.rss_kb || merged.rss_kb_last_seen || 0) / 1024)} MB</span></div>
                </div>
                {merged.error && (
                  <div className="mt-3 text-[11px] font-mono bg-white p-2 border border-red-200 text-red-800 break-all">
                    {merged.error}
                  </div>
                )}
              </div>

              {/* Process / container identity */}
              <div>
                <h3 className="text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-2">Süreç Kimliği</h3>
                <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-[11px] font-mono">
                  <div>Önceki PID: {merged.prev_pid ?? journal.process?.pid ?? "—"}</div>
                  <div>Şu anki PID: {merged.current_pid ?? current.pid ?? "—"}</div>
                  <div className="col-span-2 break-all">
                    Önceki boot_id: {merged.prev_boot_id ?? journal.process?.boot_id ?? "—"}
                  </div>
                  <div className="col-span-2 break-all">
                    Şu anki boot_id: {merged.current_boot_id ?? current.boot_id ?? "—"}
                  </div>
                  <div>Sunucu: {journal.process?.hostname ?? merged.hostname ?? "—"}</div>
                  <div>Python: {journal.process?.python ?? "—"}</div>
                </div>
              </div>

              {/* Stage history — chronological with timings */}
              <div>
                <h3 className="text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-2">Aşama Geçmişi</h3>
                {history.length === 0 ? (
                  <div className="text-[11px] text-[#A8A29E]">Aşama kaydı yok — süreç ilk yazımdan önce sonlandı.</div>
                ) : (
                  <div className="overflow-x-auto border border-[#E7E5E4]">
                    <table className="min-w-full text-[11px]">
                      <thead className="bg-[#FAFAF9] text-[9.5px] uppercase tracking-[0.15em] text-[#71717A]">
                        <tr>
                          <th className="text-left px-2 py-1.5">#</th>
                          <th className="text-left px-2 py-1.5">Aşama</th>
                          <th className="text-left px-2 py-1.5">Başlangıç</th>
                          <th className="text-right px-2 py-1.5">Süre (ms)</th>
                          <th className="text-right px-2 py-1.5">RSS Δ (KB)</th>
                          <th className="text-right px-2 py-1.5">Bitiş RSS (MB)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {history.map((h, i) => (
                          <tr key={i} className="border-t border-[#F5F5F4] font-mono">
                            <td className="px-2 py-1">{i + 1}</td>
                            <td className="px-2 py-1">{h.phase}{!h.ended_at && <span className="ml-1 text-red-600">◆</span>}</td>
                            <td className="px-2 py-1 text-[10px]">{h.started_at ? new Date(h.started_at).toLocaleTimeString("tr-TR") : "—"}</td>
                            <td className="px-2 py-1 text-right">{h.ms ?? "—"}</td>
                            <td className={"px-2 py-1 text-right " + ((h.rss_delta_kb || 0) > 50000 ? "text-red-700 font-bold" : "")}>
                              {h.rss_delta_kb != null ? (h.rss_delta_kb > 0 ? "+" : "") + h.rss_delta_kb : "—"}
                            </td>
                            <td className="px-2 py-1 text-right">{h.rss_kb_end ? Math.round(h.rss_kb_end / 1024) : "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div className="text-[9.5px] text-[#A8A29E] px-2 py-1 bg-[#FAFAF9]">◆ = aşama tamamlanmadan süreç öldürüldü</div>
                  </div>
                )}
              </div>

              {/* Raw traceback */}
              {merged.error_traceback && (
                <div>
                  <h3 className="text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-2">
                    Python Traceback ({merged.error_type || "—"})
                  </h3>
                  <pre className="text-[10px] font-mono bg-[#0A0A0A] text-[#E7E5E4] p-3 overflow-x-auto whitespace-pre max-h-64">
                    {merged.error_traceback}
                  </pre>
                </div>
              )}

              {/* Stage timings dict for completeness */}
              {Object.keys(timings).length > 0 && (
                <details>
                  <summary className="text-[10px] uppercase tracking-[0.2em] text-[#71717A] cursor-pointer">
                    Ham Zaman Verisi (stage_timings)
                  </summary>
                  <pre className="mt-2 text-[10px] font-mono bg-[#FAFAF9] p-3 overflow-x-auto max-h-40 border border-[#E7E5E4]">
                    {JSON.stringify(timings, null, 2)}
                  </pre>
                </details>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
